export interface Notification {
	id: string;
	userId: string;
	read: boolean;
	creationDateTime: string;
	category: NotificationCategory;
	objectId: string;
	/**
	 * Interpolation values for the category's translated wording (org name, document
	 * title, count). Absent on the three original categories, whose text needs none.
	 */
	params?: NotificationParams;
	/**
	 * The sending organisation's own explanation for withdrawing, replacing or revoking.
	 * Written by one party and shown to another, so it is capped server-side and must be
	 * escaped at render. Untranslatable by nature — rendered quoted and attributed.
	 */
	reason?: string;
}

export interface NotificationParams {
	orgName?: string;
	documentTitle?: string;
	documentCount?: number;
}

export enum NotificationCategory {
	SharedWallet = 'sharedwallet',
	Verification = 'verification',
	OrgInvite = 'orgInvite',
	/** An organisation sent one or more documents to this wallet, awaiting accept/reject. */
	IncomingDocument = 'incomingDocument',
	/** An organisation withdrew a document the user had accepted; it has been removed. */
	DocumentWithdrawn = 'documentWithdrawn',
	/** An organisation replaced the contents of a document the user had accepted. */
	DocumentReplaced = 'documentReplaced',
	/** An organisation revoked the verification it had applied to a document. */
	VerificationRevoked = 'verificationRevoked',
	/** An organisation changed the verification it had applied to a document. */
	VerificationChanged = 'verificationChanged',
	/**
	 * A share the user themselves made was closed, because the issuing organisation
	 * withdrew or replaced the document behind it. Distinct from DocumentWithdrawn: the
	 * user may have sent it somewhere that now matters to them.
	 */
	ShareWithdrawn = 'shareWithdrawn',
	/**
	 * An organisation changed what the user may do with a document it sent: viewing, or
	 * downloading and sharing (PTEC-896). Left out of the cleared-on-visit list on purpose:
	 * the user dismisses it themselves from the homepage.
	 */
	DocumentAccessChanged = 'documentAccessChanged'
}
//use list.length
export type NotificationCount = {
	[objectId: string]: number;
};

/**
 * Categories the wallet answers for itself, cleared when the reader opens it.
 *
 * Arriving at the wallet *is* the notification being read: the document is sitting there in
 * the state the notification describes, so making the reader dismiss it by hand would be
 * asking them to acknowledge something twice.
 *
 * This is a convenience on top of dismissal, not a substitute for it — every notification can
 * be dismissed from the homepage regardless. Categories left out of this list simply wait for
 * the reader, which is the right default: the wallet showing a document is not evidence the
 * reader knows a *different* document was withdrawn, replaced, or had a share closed.
 *
 * Only ever the wallet's own categories. Adding one that belongs to another page — a pending
 * wallet-access request, say — would delete the notice for something still outstanding the
 * moment the reader glanced at their documents.
 */
export const NOTIFICATIONS_CLEARED_ON_WALLET_VISIT: NotificationCategory[] = [
	NotificationCategory.IncomingDocument,
	NotificationCategory.VerificationChanged,
	NotificationCategory.VerificationRevoked
];
